import { getBlogNavInitialData } from '@/api/blog/blogNav';
import { getFeaturedContent } from '@/api/blog/featuredContent';
import { getPostsFeatured } from '@/api/posts/postsFeatured';
import { getLatestPosts } from '@/api/posts/postsLatest';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { BlogLayout } from '@/components/layout/withBlogLayout';
import { BlogHomepage } from '@gik/blog/components/BlogHomepage/BlogHomepage';
import type { StaticProps } from '@gik/core/api/BaseAPIConfig';
import { baseFallback } from '@gik/core/api/BaseAPIConfig';
import { withTitle } from '@gik/core/hocs/withTitle';
import { catchSSRError } from '@gik/core/utils/catchSSRError';
import { measureSSRPerformance } from '@gik/core/utils/measureSSRPerformance';
import React from 'react';
import { SWRConfig } from 'swr';

export default function BlogHomepageComp({ fallback }) {
  return withTitle(
    'Blog',
    <SWRConfig value={{ fallback }}>
      <BlogHomepage />
    </SWRConfig>
  );
}

BlogHomepageComp.getLayout = function getLayout(page: React.ReactElement, layoutProps: PageLayoutProps) {
  return <BlogLayout {...layoutProps}>{page}</BlogLayout>;
};

async function _getStaticProps(): Promise<StaticProps> {
  const carouselArticlesResponse = getPostsFeatured({
    slug: 'learn-page-primary-carousel',
    perPage: 5,
    fields: BlogHomepage.requiredWPFields.carouselArticles,
  });

  const latestArticlesResponse = getLatestPosts({ fields: BlogHomepage.requiredWPFields.latestArticles });

  const featuredContentResponse = getFeaturedContent({
    _fields: BlogHomepage.requiredWPFields.featuredContent,
  });

  const featuredArticlesGrid1Response = getPostsFeatured({
    slug: 'learn-page-row-2',
    perPage: 4,
    fields: BlogHomepage.requiredWPFields.featuredArticlesGrid,
  });

  const featuredArticlesGrid2Response = getPostsFeatured({
    slug: 'learn-page-row-3',
    perPage: 2,
    fields: BlogHomepage.requiredWPFields.featuredArticlesGrid,
  });

  const featuredArticlesGrid3Response = getPostsFeatured({
    slug: 'learn-page-row-4-col-1',
    perPage: 2,
    fields: BlogHomepage.requiredWPFields.featuredArticlesGrid,
  });

  const featuredArticlesGrid4Response = getPostsFeatured({
    slug: 'learn-page-row-4-col-2',
    perPage: 2,
    fields: BlogHomepage.requiredWPFields.featuredArticlesGrid,
  });

  const heroResponse = getPostsFeatured({
    slug: 'learn-page-bottom-card',
    perPage: 1,
    fields: BlogHomepage.requiredWPFields.hero,
  });

  const { situationsResponse, tagsResponse } = getBlogNavInitialData();

  const promises = [
    carouselArticlesResponse.json,
    latestArticlesResponse.json,
    featuredContentResponse.json,
    featuredArticlesGrid1Response.json,
    featuredArticlesGrid2Response.json,
    featuredArticlesGrid3Response.json,
    featuredArticlesGrid4Response.json,
    heroResponse.json,
    situationsResponse.json,
    tagsResponse.json,
  ];

  // @ts-ignore
  const [
    carouselArticles,
    latestArticles,
    featuredContent,
    featuredArticlesGrid1,
    featuredArticlesGrid2,
    featuredArticlesGrid3,
    featuredArticlesGrid4,
    hero,
    situations,
    tags,
    // eslint-disable-next-line
  ] = await Promise.all(promises as unknown as any);

  return baseFallback({
    [carouselArticlesResponse.key]: carouselArticles,
    [latestArticlesResponse.key]: latestArticles,
    [featuredContentResponse.key]: featuredContent,
    [featuredArticlesGrid1Response.key]: featuredArticlesGrid1,
    [featuredArticlesGrid2Response.key]: featuredArticlesGrid2,
    [featuredArticlesGrid3Response.key]: featuredArticlesGrid3,
    [featuredArticlesGrid4Response.key]: featuredArticlesGrid4,
    [heroResponse.key]: hero,
    [situationsResponse.key]: situations,
    [tagsResponse.key]: tags,
  });
}

export const getStaticProps = catchSSRError(measureSSRPerformance(_getStaticProps));
